/**
 * The casefile tab's copy, as pure functions (docs/ux/casefile.md, sentence
 * table): what the machine decided, what the human decided, the Accept label
 * per category, the decision word and tone, and the viewer plan for an item.
 * Pure so a test can assert the words and prove that nothing but manifest
 * titles, sections, providers, dates and page numbers reaches a sentence.
 */

import { DATE_TO_CHECK, PROVIDER_GROUPED, SECTION_ORDER, formatDocDate, pageAccounting, retitle } from '@beam/core/casefile';
import type { CasefileDocument, CasefileManifest, DocDate, SortCorrection, SortItemCategory, SortReviewItem } from '@/app/types';

export const CATEGORY_LABEL: Record<SortItemCategory, string> = {
  'low-confidence-boundary': 'Document boundary',
  'low-confidence-section': 'Section',
  'ambiguous-near-duplicate': 'Possible duplicate',
  'undated-document': 'Undated document',
  'unattributed-provider': 'Unattributed provider',
  'low-confidence-ocr': 'Low-confidence OCR',
  additional: 'Additional',
};

export const SECTIONS: readonly string[] = SECTION_ORDER;
export const GROUPED_SECTIONS: ReadonlySet<string> = PROVIDER_GROUPED;
export { DATE_TO_CHECK, formatDocDate, retitle };

/** The decision vocabulary both review tabs share: the casefile's five words and the LOI review's Unresolved, Missing and Flagged. */
export type DecisionWord = 'Undecided' | 'Accepted' | 'Corrected' | 'Escalated' | 'Withdrawn' | 'Unresolved' | 'Missing' | 'Flagged';

export function pagesText(from: number, to: number): string {
  return from === to ? `Page ${from}` : `Pages ${from} to ${to}`;
}

export const pagesLower = (from: number, to: number): string => pagesText(from, to).replace(/^P/, 'p');

export const pageCount = (doc: Pick<CasefileDocument, 'startPage' | 'endPage'>): number => doc.endPage - doc.startPage + 1;

export const docById = (manifest: CasefileManifest, id: string | undefined): CasefileDocument | undefined => (id ? manifest.documents.find((d) => d.id === id) : undefined);

/** The document whose range ends immediately before this one starts. */
export const previousDoc = (manifest: CasefileManifest, doc: CasefileDocument): CasefileDocument | undefined => manifest.documents.find((d) => d.endPage + 1 === doc.startPage);

export const docLine = (doc: CasefileDocument): string => `${doc.title} (${pagesLower(doc.startPage, doc.endPage)})`;

/** The retained document a copy points at, when the document is filed as a copy. */
export const copyOf = (manifest: CasefileManifest, doc: CasefileDocument | undefined): CasefileDocument | undefined => (doc?.duplicateOf ? docById(manifest, doc.duplicateOf) : undefined);

/**
 * Where a document is filed, said so a reviewer can check it: a copy names
 * the document it copies, because "Filed under Duplicates" on its own sent a
 * reviewer looking for a section for a letter the bundle carried twice.
 */
export function filedUnder(manifest: CasefileManifest, doc: CasefileDocument | undefined): string {
  if (!doc) return 'an unknown section';
  const original = copyOf(manifest, doc);
  return original ? `Duplicates as a copy of ${docLine(original)}` : doc.section;
}

/** The document an item is about: the first of its ids, or the one covering its first page. */
export function itemDoc(item: SortReviewItem, manifest: CasefileManifest): CasefileDocument | undefined {
  return docById(manifest, item.documentIds[0]) ?? manifest.documents.find((d) => item.startPage >= d.startPage && item.startPage <= d.endPage);
}

export function decisionWord(item: SortReviewItem): DecisionWord {
  switch (item.decision) {
    case 'accepted': return item.category === 'additional' ? 'Withdrawn' : 'Accepted';
    case 'corrected': return 'Corrected';
    case 'escalated': return 'Escalated';
    default: return 'Undecided';
  }
}

export const isEscalationWorthy = (item: SortReviewItem, threshold: number): boolean => item.category !== 'additional' && item.confidence < threshold;

export const confidenceText = (n: number): string => n.toFixed(2);

/** The first words of a note for a one-line sentence: cut at a word boundary near 80 characters, with an ellipsis when cut. */
export function shortNote(note: string, max = 80): string {
  if (note.length <= max) return note;
  const cut = note.slice(0, max);
  const space = cut.lastIndexOf(' ');
  return `${(space > max / 2 ? cut.slice(0, space) : cut).trimEnd()}…`;
}

export function describeMachine(item: SortReviewItem, manifest: CasefileManifest): { sentence: string; sub: string } {
  const doc = itemDoc(item, manifest);
  switch (item.category) {
    case 'low-confidence-boundary': {
      const previous = doc ? previousDoc(manifest, doc) : undefined;
      return {
        sentence: `New document starts at page ${item.startPage}.`,
        sub: doc ? `${docLine(doc)}.${previous ? ` Before it: ${docLine(previous)}.` : ''}` : '',
      };
    }
    case 'low-confidence-section':
      return { sentence: `Filed under ${filedUnder(manifest, doc)}.`, sub: doc ? docLine(doc) : '' };
    case 'ambiguous-near-duplicate': {
      const [a, b] = [docById(manifest, item.documentIds[0]), docById(manifest, item.documentIds[1])];
      return {
        sentence: `Pages ${item.startPage} and ${item.endPage} treated as distinct; both billable.`,
        sub: [a, b].filter((d): d is CasefileDocument => Boolean(d)).map(docLine).join('. '),
      };
    }
    case 'undated-document':
      return { sentence: `No date read: ${DATE_TO_CHECK}; sorts last in its group.`, sub: doc ? docLine(doc) : '' };
    case 'unattributed-provider':
      return { sentence: `No provider identified: under Unattributed in ${doc?.section ?? 'its section'}.`, sub: doc ? docLine(doc) : '' };
    case 'low-confidence-ocr':
      return {
        sentence: doc ? `Filed as ${doc.title} under ${filedUnder(manifest, doc)}, from a page read by OCR at ${confidenceText(item.confidence)}.` : `Page ${item.startPage} read by OCR at ${confidenceText(item.confidence)}.`,
        sub: doc ? docLine(doc) : '',
      };
    case 'additional':
      return { sentence: doc ? `Filed under ${doc.section} as ${doc.title} (${pagesLower(doc.startPage, doc.endPage)}).` : 'Document not in the manifest.', sub: '' };
    default:
      return { sentence: '', sub: '' };
  }
}

/** What a correction does, in one sentence; shared by the human cell and the "What changed" table. */
export function correctionSentence(c: SortCorrection, manifest: CasefileManifest): string {
  const doc = docById(manifest, c.documentId);
  switch (c.kind) {
    case 'merge': {
      const into = docById(manifest, c.intoDocumentId);
      return into && doc ? `merged with the document before; pages ${into.startPage} to ${doc.endPage} are one document.` : 'merged with the document before.';
    }
    case 'move_boundary':
      return `the document starts at page ${c.toPage}, not page ${doc?.startPage ?? '?'}.`;
    case 'split':
      return `split at page ${c.atPage}.`;
    case 'reclassify':
      return `reclassified to ${c.section}.`;
    case 'duplicate_of': {
      const pages = doc ? pageCount(doc) : 0;
      return `the later document is a duplicate of the earlier; ${pages} ${pages === 1 ? 'page moves' : 'pages move'} to Duplicates.`;
    }
    case 'not_duplicate':
      return `not a duplicate; returned to ${c.section ?? 'its section'}.`;
    case 'set_date':
      return `dated ${formatDocDate(c.date)}.`;
    case 'set_provider':
      return `provider set to ${c.provider}.`;
    default:
      return 'corrected.';
  }
}

export function describeHuman(item: SortReviewItem, manifest: CasefileManifest): { sentence: string; word: DecisionWord } {
  const word = decisionWord(item);
  const doc = itemDoc(item, manifest);
  switch (item.decision) {
    case 'unresolved':
      return { word, sentence: item.category === 'additional' ? 'Not raised.' : 'Not decided.' };
    case 'escalated':
      return { word, sentence: `Escalated: ${shortNote(item.note ?? '')}` };
    case 'corrected': {
      const prefix = item.category === 'low-confidence-ocr' ? `Corrected (from page ${item.startPage}): ` : 'Corrected: ';
      return { word, sentence: `${prefix}${item.correction ? correctionSentence(item.correction, manifest) : 'corrected.'}` };
    }
    case 'accepted':
    default:
      break;
  }
  switch (item.category) {
    case 'low-confidence-boundary': return { word, sentence: `Accepted: page ${item.startPage} starts a new document.` };
    case 'low-confidence-section': {
      const original = copyOf(manifest, doc);
      return { word, sentence: original ? `Accepted: a copy of ${pagesLower(original.startPage, original.endPage)}; Duplicates.` : `Accepted: ${doc?.section ?? 'the section'}.` };
    }
    case 'ambiguous-near-duplicate': return { word, sentence: 'Accepted: distinct pages; both billable.' };
    case 'undated-document': return { word, sentence: 'Accepted: the date is left to check.' };
    case 'unattributed-provider': return { word, sentence: 'Accepted: left under Unattributed.' };
    case 'low-confidence-ocr': return { word, sentence: 'Accepted: the reading and the filing stand.' };
    case 'additional': return { word, sentence: 'Withdrawn: the machine result stands.' };
    default: return { word, sentence: 'Accepted.' };
  }
}

export function acceptLabel(item: SortReviewItem, manifest: CasefileManifest): string {
  const doc = itemDoc(item, manifest);
  switch (item.category) {
    case 'low-confidence-boundary': return `Accept: page ${item.startPage} starts a new document`;
    case 'low-confidence-section': {
      const original = copyOf(manifest, doc);
      return original ? `Accept: a copy of ${pagesLower(original.startPage, original.endPage)}; filed under Duplicates` : `Accept: filed under ${doc?.section ?? 'this section'}`;
    }
    case 'ambiguous-near-duplicate': return `Accept: pages ${item.startPage} and ${item.endPage} are distinct; both billable`;
    case 'undated-document': return `Accept: leave ${DATE_TO_CHECK}; the document sorts last in its group`;
    case 'unattributed-provider': return 'Accept: leave under Unattributed';
    case 'low-confidence-ocr': return 'Accept: keep the reading; the filing stands';
    default: return 'Accept the machine result';
  }
}

export type ViewerWindow = { from: number; to: number };
export type ViewerPlan = {
  mode: 'range' | 'compare' | 'single';
  windows: ViewerWindow[];
  /** Full extent, for "Showing pages a to b of c to d". */
  extent: ViewerWindow;
  captions: Record<number, string>;
  /** Pages that begin a machine boundary: a dashed rule is drawn before them. */
  dashedBefore: number[];
};

export const VIEWER_PAGE_LIMIT = 12;

export function viewerPlan(item: SortReviewItem, manifest: CasefileManifest): ViewerPlan {
  if (item.category === 'ambiguous-near-duplicate') {
    return {
      mode: 'compare',
      windows: [{ from: item.startPage, to: item.startPage }, { from: item.endPage, to: item.endPage }],
      extent: { from: item.startPage, to: item.endPage },
      captions: { [item.startPage]: `Earlier page ${item.startPage}`, [item.endPage]: `Later page ${item.endPage}` },
      dashedBefore: [],
    };
  }
  if (item.category === 'low-confidence-ocr') {
    return { mode: 'single', windows: [{ from: item.startPage, to: item.startPage }], extent: { from: item.startPage, to: item.startPage }, captions: { [item.startPage]: `Page ${item.startPage}, read by OCR` }, dashedBefore: [] };
  }
  const captions: Record<number, string> = {};
  let from = item.startPage;
  const dashedBefore: number[] = [];
  if (item.category === 'low-confidence-boundary') {
    const doc = itemDoc(item, manifest);
    const previous = doc ? previousDoc(manifest, doc) : undefined;
    if (previous) {
      from = item.startPage - 1;
      captions[from] = `Page ${from}. Previous document ends`;
    }
    captions[item.startPage] = `Page ${item.startPage}. Machine: new document starts here`;
    dashedBefore.push(item.startPage);
  }
  const extent = { from, to: item.endPage };
  return { mode: 'range', windows: [{ from, to: Math.min(item.endPage, from + VIEWER_PAGE_LIMIT - 1) }], extent, captions, dashedBefore };
}

/** The next window after the ones shown, or null when the extent is covered. */
export function nextWindow(shown: ViewerWindow[], extent: ViewerWindow): ViewerWindow | null {
  const last = shown.at(-1);
  if (!last || last.to >= extent.to) return null;
  return { from: last.to + 1, to: Math.min(extent.to, last.to + VIEWER_PAGE_LIMIT) };
}

export function billableAfterDuplicate(manifest: CasefileManifest, laterId: string): number {
  const docs = manifest.documents.map((d) => (d.id === laterId ? { ...d, duplicateOf: 'x', section: 'Duplicates' } : d));
  return pageAccounting(docs as never, manifest.totalPages).billablePages;
}

export function previewTitle(doc: CasefileDocument, change: { date?: DocDate | null; provider?: string }): string {
  return retitle({ title: doc.title, provider: doc.provider, date: doc.date }, change);
}

export function wallClockText(ms: number): string {
  const minutes = Math.round(ms / 60_000);
  return minutes < 1 ? 'under 1 min' : `${minutes} min`;
}

export const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export function daysInMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate();
}
